import { useParams, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { LiveGame } from "../features/player/index.js";

interface PlayerLocationState {
  playerId: string;
  playerToken: string;
  nickname: string;
  initialScore?: number;
}

const STORAGE_KEY_PREFIX = "hootapp_player_";

function saveSession(sessionId: string, state: PlayerLocationState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY_PREFIX + sessionId, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable — silently skip
  }
}

function loadSession(sessionId: string): PlayerLocationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + sessionId);
    if (!raw) return null;
    return JSON.parse(raw) as PlayerLocationState;
  } catch {
    return null;
  }
}

export function clearPlayerSession(sessionId: string): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY_PREFIX + sessionId);
  } catch {
    // ignore
  }
}

export function PlayPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const locationState = location.state as PlayerLocationState | null;

  // Persist to sessionStorage when we have fresh state from navigation
  useEffect(() => {
    if (sessionId && locationState?.playerId) {
      saveSession(sessionId, locationState);
    }
  }, [sessionId, locationState]);

  if (!sessionId) {
    return <Navigate to="/join" replace />;
  }

  const state = locationState ?? (sessionId ? loadSession(sessionId) : null);

  if (!state?.playerId || !state?.playerToken || !state?.nickname) {
    return <Navigate to="/join" replace />;
  }

  return (
    <LiveGame
      sessionId={sessionId}
      playerId={state.playerId}
      playerToken={state.playerToken}
      nickname={state.nickname}
      initialScore={state.initialScore ?? 0}
    />
  );
}
